import IconCopy from '@apollo/icons/default/IconCopy.svg';
import IconEdit from '@apollo/icons/default/IconEdit.svg';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import { Button, FormControl, FormLabel, Input } from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { isEmbeddableSandboxRoute } from 'src/app/embeddableSandbox/isEmbeddableSandboxRoute';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Form } from 'src/components/common/form/Form';
import { useShowToast } from 'src/components/toast/Toast';
import { TooltipWithMutableContent } from 'src/components/TooltipWithMutableContent';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import Config from 'src/lib/config';
import { isBrave, isSafari } from 'src/lib/getBrowser';
import { isLocalUrl } from 'src/lib/isLocalUrl';
import { DocsRouter } from 'src/lib/routers';

import {
  ThemeProvider,
  useThemeContext,
} from '../../../../components/themeProvider/ThemeProvider';
import { useSetEndpointReachability } from '../../hooks/useEndpointReachability';
import { sandboxGraphRouteConfig } from '../../routes';

export const useNetworkErrorToast = ({
  setShowNetworkWarning,
  openEditUrlModal,
  triggeredBySubscription,
  introspectionDisabled,
  url,
}: {
  setShowNetworkWarning: React.Dispatch<React.SetStateAction<boolean>>;
  openEditUrlModal: (openingIntent: 'Set url' | 'Run query') => void;
  triggeredBySubscription: boolean;
  introspectionDisabled: boolean;
  url: string | null;
}) => {
  const networkToastId = 'network-error-toast-id';
  const { showToasts, hideToasts } = useShowToast();
  const { themeName } = useThemeContext();
  const [isUsingBrave, setIsUsingBrave] = useState(false);
  const isUsingSafari = isSafari();
  const isEmbedded = isEmbeddableExplorerRoute() || isEmbeddableSandboxRoute();

  const urlHasNonSecureProtocol = useMemo(
    () => url?.startsWith('http://') || url?.startsWith('ws://'),
    [url],
  );

  const hideNetworkErrorToast = useCallback(() => {
    hideToasts(networkToastId);
  }, [hideToasts]);

  useEffect(() => {
    isBrave().then((value) => {
      setIsUsingBrave(value);
    });
  }, []);

  // remove the toast on cleanup
  useEffect(() => {
    return () => {
      hideNetworkErrorToast();
    };
  }, [hideNetworkErrorToast]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log(
      `
    
       !
       !
       ^                  %cWelcome to the Explorer!%c
      / \\                 Here are some common reasons for connection issues: 
     /___\\            
    |=   =|            
    |     |               %cConnection Refused:%c
    |  %cA%c  |               - Is your GraphQL endpoint served at /graphql or /api/graphql?
    |  %cP%c  |               - Is your server running at ${url}? 
    |  %cO%c  |                
    |  %cL%c  |               %cCORS Errors:%c 
    |  %cL%c  |               - Is your endpoint sending CORS headers for studio.apollographql.com?
    |  %cO%c  |                
    |     |               %cAuthentication:%c
    |     |               - Does your endpoint require authentication?
   /|##!##|\\              
  / |##!##| \\              
 /  |##!##|  \\
|  / ^ | ^ \\  |
| /  %c(%c | %c)%c  \\ |
|/   %c(%c | %c)%c   \\|
    %c(%c(   )%c)
   (%c(%c  :%c  )%c)
   (%c(%c  :%c  )%c)
    (%c(   )%c)
     (%c( )%c)%c
      ( )
       .
       .
       .

    
    `,
      'font-weight: 700;',
      'font-weight: 400;',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #7156D9',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #7156D9',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #7156D9',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #7156D9',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #7156D9',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #7156D9',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 400; color: inherit',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 700; color: #F59140',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 700; color: #F59140',
      'font-weight: 700; color: #F4D03F',
      'font-weight: 700; color: #F59140',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 700; color: #F59140',
      'font-weight: 700; color: #F4D03F',
      'font-weight: 700; color: #F59140',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 700; color: #F59140',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 700; color: #F59140',
      'font-weight: 700; color: #D13B3B',
      'font-weight: 700; color: #F4D03F',
    );
  }, [url]);

  const setEndpointReachability = useSetEndpointReachability();
  const showNetworkErrorToast = useCallback(() => {
    showToasts({
      stableId: networkToastId,
      theme: themeName || 'dark',
      heading: `Unable to reach ${
        triggeredBySubscription ? 'subscription' : ''
      } server`,
      level: introspectionDisabled ? ('warn' as const) : ('error' as const),
      onDismiss: () => {
        setEndpointReachability('unknown');
        setShowNetworkWarning(false);
      },
      message: (
        <ThemeProvider overrideThemeName={themeName} className="size-full">
          {url &&
          isLocalUrl(url) &&
          urlHasNonSecureProtocol &&
          (isUsingBrave || isUsingSafari) ? (
            <>
              <p className="my-2 font-semibold">
                {`${isUsingBrave ? 'Brave' : 'Safari'} ${
                  isEmbedded ? 'may' : 'does'
                } not support network requests from ${
                  isEmbedded
                    ? 'this website, if it uses HTTPS,'
                    : 'Studio on HTTPS'
                } to your local ${
                  // nosemgrep
                  url?.startsWith('ws://') ? 'WS' : 'HTTP'
                }  endpoint, so we cannot reach your
                endpoint.`}
              </p>
              <p>
                {`There are a ${isUsingBrave ? 'few' : 'couple'} solutions:`}
                <ul className="my-2 ml-4 list-disc">
                  {isUsingBrave && (
                    <li className="mb-3">
                      Turn Shields down in your Brave settings for{' '}
                      <span className="font-bold">
                        https://{Config.appHost}
                      </span>
                    </li>
                  )}
                  <li className="mb-3">
                    {`Visit this page from a browser that supports HTTPS requests to
                    local ${
                      // nosemgrep
                      url?.startsWith('ws://') ? 'WS' : 'HTTP'
                    }  endpoints, like Chrome or Firefox.`}
                  </li>
                  <li className="mb-3">
                    Set up an SSL certificate for your local endpoint.
                  </li>
                </ul>
              </p>
            </>
          ) : urlHasNonSecureProtocol && url && !isLocalUrl(url) ? (
            <p className="my-4 font-semibold">
              {`Network requests from ${
                isEmbedded
                  ? 'this website, if it uses HTTPS,'
                  : 'Studio on HTTPS'
              } to your 
              ${
                // nosemgrep
                url?.startsWith('ws://') ? 'WS' : 'HTTP'
              }  endpoint ${
                isEmbedded ? 'may not be' : 'are not'
              } secure, so we cannot reach your
          endpoint.`}
            </p>
          ) : introspectionDisabled ? (
            <>
              {' '}
              <strong>We were unable to introspect your endpoint.</strong>{' '}
              Please enable introspection on your server. (You might still be
              able to submit operations!)
            </>
          ) : (
            <DiagnosticInstructions
              triggeredBySubscription={triggeredBySubscription}
              openEditUrlModal={openEditUrlModal}
              endpoint={url as string}
            />
          )}
        </ThemeProvider>
      ),
    });
  }, [
    showToasts,
    themeName,
    triggeredBySubscription,
    introspectionDisabled,
    url,
    urlHasNonSecureProtocol,
    isEmbedded,
    isUsingBrave,
    isUsingSafari,
    openEditUrlModal,
    setEndpointReachability,
    setShowNetworkWarning,
  ]);

  return React.useMemo(
    () => ({ showNetworkErrorToast, hideNetworkErrorToast }),
    [showNetworkErrorToast, hideNetworkErrorToast],
  );
};

const DiagnosticInstructions = ({
  endpoint,
  triggeredBySubscription,
  openEditUrlModal,
}: {
  endpoint: string;
  triggeredBySubscription: boolean;
  openEditUrlModal: (openingIntent: 'Set url' | 'Run query') => void;
}) => {
  const [diagnosisMethod, setDiagnosisMethod] = useState<
    'self-guided' | 'script-guided'
  >('script-guided');
  const trackCustomEvent = useTrackCustomEvent();
  const isSandboxRoute =
    useRouteMatch(sandboxGraphRouteConfig.definition) !== null;
  const isEmbed = isEmbeddableExplorerRoute() || isEmbeddableSandboxRoute();

  if (diagnosisMethod === 'script-guided') {
    return (
      <div className="flex flex-col">
        {isEmbed ? (
          <DiagnoseProblemInstructions endpoint={endpoint} />
        ) : (
          <>
            <p className="mt-2">
              <strong>Try one of these solutions:</strong>
            </p>
            <ul className="ml-4 mt-2 list-disc">
              <li className="mb-3">
                <ClickableText
                  className="text-link"
                  as="a"
                  href={
                    isSandboxRoute
                      ? DocsRouter.path('EmbeddedSandbox')
                      : DocsRouter.path('EmbeddedExplorer')
                  }
                  target="__BLANK"
                >
                  Embed {isSandboxRoute ? 'Sandbox' : 'your graph'} on your own
                  website <IconOutlink className="ml-2" />
                </ClickableText>
              </li>
              <li>
                <DiagnoseProblemInstructions endpoint={endpoint} />
              </li>
            </ul>
          </>
        )}
        <Button
          className="group ml-auto mt-2"
          variant="hidden"
          onClick={() => {
            setDiagnosisMethod('self-guided');
            trackCustomEvent({
              action: 'set_diagnosis_method',
              category: 'Explorer',
              label: 'self-guided',
            });
          }}
          data-analytics-label="prefer-self-guided-diagnostics"
          data-analytics-category="Explorer"
        >
          Prefer to self diagnose?
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      <SelfDiagnosticInstructions
        triggeredBySubscription={triggeredBySubscription}
        openEditUrlModal={openEditUrlModal}
      />
      <Button
        className="group ml-auto mt-2"
        variant="hidden"
        type="button"
        size="sm"
        onClick={() => {
          setDiagnosisMethod('script-guided');
          trackCustomEvent({
            action: 'set_diagnosis_method',
            category: 'Explorer',
            label: 'script-guided',
          });
        }}
        data-analytics-label="prefer-script-guided-diagnostics"
      >
        Prefer guided diagnostics?
      </Button>
    </div>
  );
};

const DiagnoseProblemInstructions = ({ endpoint }: { endpoint: string }) => {
  const trackCustomEvent = useTrackCustomEvent();
  const diagnosisScript = `npx diagnose-endpoint@1.1.0 --endpoint="${endpoint}"`;
  return (
    <>
      To diagnose the problem, please run:
      <TooltipWithMutableContent initialContent="Copy to clipboard">
        {({ setContent, resetContent }) => (
          <Button
            className={classnames(
              '-mx-1 mt-2 px-1',
              // override space-kit Button styles
              'flex h-auto max-w-full whitespace-normal p-0 text-left font-normal',
            )}
            size="sm"
            variant="hidden"
            onClick={() => {
              copy(diagnosisScript);
              setContent('Copied');
              trackCustomEvent({
                action: 'copy_diagnosis_script',
                category: 'Explorer',
              });
            }}
            onMouseEnter={() => resetContent()}
          >
            <code className="break-all text-secondary">
              {diagnosisScript}
              <IconCopy className="ml-2" />
            </code>
          </Button>
        )}
      </TooltipWithMutableContent>
    </>
  );
};

const SelfDiagnosticInstructions = ({
  triggeredBySubscription,
  openEditUrlModal,
}: {
  triggeredBySubscription: boolean;
  openEditUrlModal: (openingIntent: 'Set url' | 'Run query') => void;
}) => {
  return (
    <>
      <ul className="my-2 ml-4 list-disc">
        <li className="mb-3">
          <p>
            <strong>The server's response may be missing CORS headers.</strong>
          </p>{' '}
          <p>
            To verify, please open Developer Tools and check the console for any
            CORS related error messages, like “Cross Origin Request Blocked”.
          </p>{' '}
          <p>
            If that is the case, contact your system administrator and add{' '}
            <span className="font-bold">https://{Config.appHost}</span> to your
            server's allow-list.{' '}
            <p>
              <ClickableText
                className="text-link"
                as="a"
                href="https://enable-cors.org/server.html"
                target="__BLANK"
              >
                Learn more about enabling CORS <IconOutlink />
              </ClickableText>
            </p>
          </p>
        </li>
        <li className="mb-3">
          {`The server may be not reachable at the provided ${
            triggeredBySubscription ? 'subscription' : ''
          } endpoint. Please
        double check your configured ${
          triggeredBySubscription ? 'subscription' : ''
        } endpoint URL for typos.`}
          <span
            className="flex cursor-pointer items-center text-link underline"
            onClick={() => openEditUrlModal('Set url')}
          >
            {`Check the ${
              triggeredBySubscription ? 'subscription' : ''
            } endpoint URL `}
            <IconEdit className="ml-1" />
          </span>
        </li>
        <li>Is the server currently running?</li>
      </ul>
      <LocalAgentFeedbackCollector />
    </>
  );
};

const LocalAgentFeedbackCollector = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmittedEmail, setHasSubmittedEmail] = useLocalStorage(
    'hasSubmittedEmailToLocalAgentInterestForm',
  );

  if (hasSubmittedEmail) {
    return (
      <div className="text-sm text-primary">
        Thank you for your interest in local agent, we'll reach out when it
        becomes available.
      </div>
    );
  }
  return (
    <Form
      className="mt-2"
      onSubmit={async () => {
        setIsSubmitting(true);

        // the gibberish part of the url https://docs.google.com/forms/u/0/d/e/1FAIpQLScUCi3PdMerraiy6GpD-QiC_9KEKVHr4oDL5Vef5fIvzqqQWg/formResponse
        const formId = `1FAIpQLScUCi3PdMerraiy6GpD-QiC_9KEKVHr4oDL5Vef5fIvzqqQWg`;
        // submit the form, look at the network response's request headers to find this id from the Form Data section of the request
        const formField = `entry.1905536063`;

        await fetch(
          `https://docs.google.com/forms/u/0/d/e/${formId}/formResponse?usp=pp_url&${formField}=${encodeURIComponent(
            email,
          )}`,
          {
            method: 'POST',
            mode: 'no-cors',
          },
        );

        setIsSubmitting(false);
        setHasSubmittedEmail(true);
      }}
    >
      <FormControl>
        <FormLabel>
          📫 We are investing in a local tunnel to bypass CORS requirements.
          Should we drop a note when it's ready?
        </FormLabel>
        <Input
          isDisabled={isSubmitting}
          name="id"
          size="sm"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          placeholder="abc@xyz.com"
        />
      </FormControl>
      <Button
        type="submit"
        isDisabled={isSubmitting}
        size="sm"
        className="mt-2"
      >
        {isSubmitting ? 'submitting...' : 'Yes, please! Email me.'}
      </Button>
    </Form>
  );
};
